import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { RiderLayout } from "@/components/rider/RiderLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, CreditCard } from "lucide-react";
import { StripeProvider } from "@/components/rider/quote/StripeProvider";
import { CardElement, useStripe, useElements, PaymentRequestButtonElement } from "@stripe/react-stripe-js";
import { useToast } from "@/hooks/use-toast";
import { motion } from "framer-motion";
import { API_URL } from "@/config/api";

interface UnbookedTripData {
  id: string;
  pickup_distance: number | null;
  trip_length: number | null;
  round_trip: boolean;
  community_event: any | null;
  created: string;
  modified: string;
  claimed_at: string | null;
  booking_duration: string;
  passenger_count: number;
  rider_price_currency: string;
  rider_price: string;
  extra: any;
  pickup_at: string;
  from_location: string;
  to_location: string;
  started_at: string | null;
  ended_at: string | null;
  return_trip_pickup_at: string | null;
  return_trip_from_location: string | null;
  return_trip_to_location: string | null;
  return_trip_started_at: string | null;
  return_trip_ended_at: string | null;
  rider: string;
  claimed_by: string | null;
  stripe_charge: string | null;
  to_address: {
    id: number;
    locality: {
      name: string;
      postal_code: string;
    };
    state: {
      name: string;
      code: string;
    };
    country: {
      name: string;
      code: string;
    };
    street_number: string;
    route: string;
    raw: string;
    formatted: string;
    latitude: number;
    longitude: number;
  };
  from_address: {
    id: number;
    locality: {
      name: string;
      postal_code: string;
    };
    state: {
      name: string;
      code: string;
    };
    country: {
      name: string;
      code: string;
    };
    street_number: string;
    route: string;
    raw: string;
    formatted: string;
    latitude: number;
    longitude: number;
  };
}

const CheckoutForm = ({ tripData }: { tripData: UnbookedTripData | null }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  console.log('CheckoutForm rendered', { 
    stripe: !!stripe, 
    elements: !!elements, 
    tripData: !!tripData,
    tripPrice: tripData?.rider_price 
  });
  
  const [formData, setFormData] = useState({
    billingAddress: {
      line1: "",
      city: "",
      state: "",
      postal_code: "",
      country: "US"
    }
  });
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState<any>(null);
  const [canMakePayment, setCanMakePayment] = useState(false);

  // Initialize Payment Request (Apple Pay/Google Pay)
  useEffect(() => {
    console.log('Payment Request useEffect running', { stripe: !!stripe, tripData: !!tripData });
    
    if (!stripe || !tripData) {
      console.log('Missing dependencies for Payment Request:', { stripe: !!stripe, tripData: !!tripData });
      return;
    }

    console.log('Creating Payment Request with amount:', Math.round(parseFloat(tripData.rider_price) * 100));
    
    // Create payment request according to Stripe docs
    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: 'Party Bus Trip',
        amount: Math.round(parseFloat(tripData.rider_price) * 100),
      },
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
    });

    console.log('Payment Request created, checking availability...');

    // Check if Apple Pay/Google Pay is available
    pr.canMakePayment().then((result) => {
      console.log('=== APPLE PAY DEBUG ===');
      console.log('Payment Request availability result:', result);
      console.log('User agent:', navigator.userAgent);
      console.log('Is HTTPS:', window.location.protocol === 'https:');
      console.log('Current URL:', window.location.href);
      console.log('Is Safari:', /^((?!chrome|android).)*safari/i.test(navigator.userAgent));
      
      if (result) {
        console.log('✅ Apple Pay/Google Pay is available! Setting up...');
        setPaymentRequest(pr);
        setCanMakePayment(true);
      } else {
        console.log('❌ Apple Pay/Google Pay not available');
        console.log('Possible reasons:');
        console.log('- Not Safari/iOS or supported browser');
        console.log('- No payment methods set up in Wallet');
        console.log('- Domain not verified with Apple Pay');
        console.log('- Not HTTPS');
      }
    }).catch((error) => {
      console.error('❌ Error checking payment availability:', error);
    });

    // Handle payment method event
    pr.on('paymentmethod', async (event) => {
      console.log('🍎 Apple Pay payment method received:', event);
      
      try {
        setIsProcessing(true);
        
        // Create payment intent for Apple Pay
        console.log('Creating payment intent for Apple Pay...');
        const paymentIntentResponse = await fetch(`${API_URL}stripe/payment-intent/`, {
          method: "POST",
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json"
          },
          mode: "cors",
          body: JSON.stringify({
            currency: "usd",
            metadata: {
              unbooked_trip_id: tripData.id
            }
          })
        });

        if (!paymentIntentResponse.ok) {
          throw new Error('Failed to create payment intent');
        }

        const paymentIntentData = await paymentIntentResponse.json();
        const clientSecret = paymentIntentData.clientSecret || paymentIntentData.client_secret;
        const authToken = paymentIntentData.token;

        if (!clientSecret) {
          throw new Error("No client secret received from payment intent");
        }

        console.log('Confirming Apple Pay payment...');
        
        // Confirm payment with Apple Pay
        const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: event.paymentMethod.id,
        });

        if (error) {
          console.error('Apple Pay payment failed:', error);
          event.complete('fail');
          toast({
            title: "Payment failed",
            description: error.message,
            variant: "destructive"
          });
        } else if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture')) {
          console.log('✅ Apple Pay payment succeeded!');
          event.complete('success');
          toast({
            title: "Payment successful!",
            description: "Your party bus has been booked successfully"
          });
          
          // Store the authentication token if provided
          if (authToken) {
            localStorage.setItem('authToken', authToken);
            console.log('Auth token stored for authenticated user session');
          }
          
          navigate('/rider-my-trips');
        }
      } catch (error: any) {
        console.error('Apple Pay error:', error);
        event.complete('fail');
        toast({
          title: "Payment failed",
          description: error.message || "An error occurred during payment processing",
          variant: "destructive"
        });
      } finally {
        setIsProcessing(false);
      }
    });
  }, [stripe, tripData, toast, navigate]);

  const handleInputChange = (field: string, value: string | boolean) => {
    if (field.startsWith('billing.')) {
      const billingField = field.replace('billing.', '');
      setFormData(prev => ({
        ...prev,
        billingAddress: {
          ...prev.billingAddress,
          [billingField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    if (!formData.billingAddress.line1 || !formData.billingAddress.city || !formData.billingAddress.state || !formData.billingAddress.postal_code) {
      toast({
        title: "Missing information",
        description: "Please fill out all required billing address fields",
        variant: "destructive"
      });
      return;
    }

    if (!tripData) {
      toast({
        title: "Error",
        description: "Trip data not found",
        variant: "destructive"
      });
      return;
    }

    setIsProcessing(true);

    try {
      // Step 1: Create payment intent
      const paymentIntentResponse = await fetch(`${API_URL}stripe/payment-intent/`, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json"
        },
        mode: "cors",
        body: JSON.stringify({
          currency: "usd",
          metadata: {
            unbooked_trip_id: tripData.id
          }
        })
      });

      if (!paymentIntentResponse.ok) {
        // Try to extract error details from JSON response
        let errorMessage = `Payment intent creation failed: ${paymentIntentResponse.status}`;
        try {
          const errorData = await paymentIntentResponse.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (jsonError) {
          // If we can't parse JSON, use the status-based message
        }
        throw new Error(errorMessage);
      }

      const paymentIntentData = await paymentIntentResponse.json();
      // Handle both clientSecret and client_secret property names
      const clientSecret = paymentIntentData.clientSecret || paymentIntentData.client_secret;
      const authToken = paymentIntentData.token;

      if (!clientSecret) {
        throw new Error("No client secret received from payment intent");
      }

      // Step 2: Confirm payment with Stripe
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error("Card element not found");
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            address: {
              line1: formData.billingAddress.line1,
              city: formData.billingAddress.city,
              state: formData.billingAddress.state,
              postal_code: formData.billingAddress.postal_code,
              country: formData.billingAddress.country,
            },
          },
        },
      });

      if (error) {
        throw new Error(error.message || "Payment confirmation failed");
      }

      if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture')) {
        toast({
          title: "Payment successful!",
          description: "Your party bus has been booked successfully"
        });
        
        // Store the authentication token if provided
        if (authToken) {
          localStorage.setItem('authToken', authToken);
          console.log('Auth token stored for authenticated user session');
        }
        
        navigate('/rider-my-trips');
        
        // Scroll to top on mobile after navigation
        if (window.innerWidth < 768) {
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 100);
        }
      } else {
        throw new Error("Payment was not successful");
      }
      
    } catch (error: any) {
      console.error("Payment error:", error);
      toast({
        title: "Payment failed",
        description: error.message || "An error occurred during payment processing",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (!tripData) {
    return <div className="text-center">Loading trip details...</div>;
  }

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDuration = (duration: string) => {
    // Convert "04:00:00" format to "4 hours"
    const [hours] = duration.split(':');
    return `${parseInt(hours)} hours`;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Apple Pay/Google Pay */}
      {canMakePayment && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-xl font-semibold text-black mb-4">Express Checkout</h3>
          <PaymentRequestButtonElement 
            options={{
              paymentRequest,
              style: {
                paymentRequestButton: {
                  type: 'default',
                  theme: 'dark',
                  height: '48px',
                },
              },
            }}
          />
          <div className="flex items-center my-4">
            <div className="flex-1 border-t border-gray-200"></div>
            <span className="px-4 text-gray-500 text-sm">or pay with card</span>
            <div className="flex-1 border-t border-gray-200"></div>
          </div>
        </div>
      )}

      {/* Payment Information */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-xl font-semibold text-black mb-4 flex items-center">
          <CreditCard className="mr-2 h-5 w-5" />
          Payment Information
        </h3>
        <div className="border border-gray-200 p-4 rounded-md bg-white">
          <CardElement 
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
                invalid: {
                  color: '#9e2146',
                },
              },
            }}
          />
        </div>
      </div>

      {/* Billing Address */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-xl font-semibold text-black mb-4">Billing Address</h3>
        <div className="space-y-4">
          <Input
            placeholder="Address Line 1 *"
            value={formData.billingAddress.line1}
            onChange={(e) => handleInputChange('billing.line1', e.target.value)}
            className="bg-white text-black"
            required
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              placeholder="City *"
              value={formData.billingAddress.city}
              onChange={(e) => handleInputChange('billing.city', e.target.value)}
              className="bg-white text-black"
              required
            />
            <Input
              placeholder="State *"
              value={formData.billingAddress.state}
              onChange={(e) => handleInputChange('billing.state', e.target.value)}
              className="bg-white text-black"
              required
            />
            <Input
              placeholder="ZIP Code *"
              value={formData.billingAddress.postal_code}
              onChange={(e) => handleInputChange('billing.postal_code', e.target.value)}
              className="bg-white text-black"
              required
            />
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <Button 
        type="submit" 
        disabled={!stripe || isProcessing}
        className="w-full bg-black text-white py-6 rounded-full text-xl hover:bg-black/90"
      >
        {isProcessing ? 'Processing...' : `Complete Payment - $${parseFloat(tripData.rider_price).toFixed(2)}`}
      </Button>

      {/* Terms & Conditions and Cancellation Policy - Mobile Only */}
      <div className="lg:hidden bg-gray-50 rounded-xl p-6 border border-gray-100 space-y-6">
        <div>
          <h4 className="text-lg font-semibold text-black mb-3">Terms & Conditions</h4>
          <div className="text-sm text-gray-600 space-y-2">
            <p>• By purchasing a trip with Disco you expressly consent to the <a href="#" className="text-black underline">Terms & Conditions</a></p>
            <p>• Disco is a Concierge provider that works with various operators to ensure service</p>
            <p>• All operators are required to maintain their own insurance and are liable for any incident that occurs during the course of the riders trip</p>
            <p>• The rider agrees to indemnify Disco for any incident that occurs during the course of a riders trip</p>
          </div>
        </div>
        
        <div>
          <h4 className="text-lg font-semibold text-black mb-3">Cancellation Policy</h4>
          <div className="text-sm text-gray-600 space-y-2">
            <p>• All cancelations must be made 72 hours before the start of the trip</p>
            <p>• Any cancelation will incur a $10 non-refundable service fee</p>
            <p>• To cancel your trip please contact the 'HELP' number listed on a users trips page</p>
          </div>
        </div>
      </div>
    </form>
  );
};

const RiderCheckout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  
  const [tripData, setTripData] = useState<UnbookedTripData | null>(null);
  const [loading, setLoading] = useState(true);

  // Extract unbooked_trip_id from URL query string
  const unbookedTripId = searchParams.get('unbooked_trip_id');

  useEffect(() => {
    const fetchTripData = async () => {
      if (!unbookedTripId) {
        toast({
          title: "Missing trip ID",
          description: "No trip ID found in URL",
          variant: "destructive"
        });
        navigate('/');
        return;
      }

      try {
        const response = await fetch(`${API_URL}trips/unbooked/${unbookedTripId}/`, {
          method: "GET",
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json"
          },
          mode: "cors"
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch trip data: ${response.status}`);
        }

        const data: UnbookedTripData = await response.json();
        console.log("Fetched trip data:", data);
        setTripData(data);
      } catch (error: any) {
        console.error("Error fetching trip data:", error);
        toast({
          title: "Error loading trip",
          description: error.message || "Failed to load trip details",
          variant: "destructive"
        });
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    fetchTripData();
  }, [unbookedTripId, navigate, toast]);

  const handleBackNavigation = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <RiderLayout>
        <div className="container mx-auto px-4 py-6 max-w-4xl">
          <div className="text-center">Loading trip details...</div>
        </div>
      </RiderLayout>
    );
  }

  if (!tripData) {
    return (
      <RiderLayout>
        <div className="container mx-auto px-4 py-6 max-w-4xl">
          <div className="text-center">Trip not found</div>
        </div>
      </RiderLayout>
    );
  }

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDuration = (duration: string) => {
    // Convert "04:00:00" format to "4 hours"
    const [hours] = duration.split(':');
    return `${parseInt(hours)} hours`;
  };

  return (
    <RiderLayout>
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <div className="flex justify-between items-center mb-8">
          <button 
            onClick={handleBackNavigation} 
            className="flex items-center text-black hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="mr-1 h-5 w-5" />
            <span>Back</span>
          </button>
          <motion.h1 
            className="text-3xl font-bold text-center text-black"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            Checkout
          </motion.h1>
          <div></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Trip Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 sticky top-6">
              <h3 className="text-xl font-semibold text-black mb-4">Trip Summary</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Pickup Time:</span>
                  <span className="text-black font-medium">{formatDateTime(tripData.pickup_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">From:</span>
                  <span className="text-black">{tripData.from_address.formatted}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">To:</span>
                  <span className="text-black">{tripData.to_address.formatted}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Duration:</span>
                  <span className="text-black">{formatDuration(tripData.booking_duration)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Capacity:</span>
                  <span className="text-black">{tripData.passenger_count} people</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Trip Type:</span>
                  <span className="text-black">{tripData.round_trip ? 'Round Trip' : 'One-Way'}</span>
                </div>
                <hr className="my-4" />
                <div className="flex justify-between text-lg font-bold">
                  <span className="text-black">Total:</span>
                  <span className="text-black">${parseFloat(tripData.rider_price).toFixed(2)} {tripData.rider_price_currency}</span>
                </div>
              </div>
              
              {/* Terms & Conditions and Cancellation Policy - Desktop Only */}
              <div className="hidden lg:block mt-6 pt-6 border-t border-gray-200 space-y-4">
                <div>
                  <h4 className="text-lg font-semibold text-black mb-3">Terms & Conditions</h4>
                  <div className="text-xs text-gray-600 space-y-1">
                    <p>• By purchasing a trip with Disco you expressly consent to the <a href="#" className="text-black underline">Terms & Conditions</a></p>
                    <p>• Disco is a Concierge provider that works with various operators to ensure service</p>
                    <p>• All operators are required to maintain their own insurance and are liable for any incident that occurs during the course of the riders trip</p>
                    <p>• The rider agrees to indemnify Disco for any incident that occurs during the course of a riders trip</p>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-lg font-semibold text-black mb-3">Cancellation Policy</h4>
                  <div className="text-xs text-gray-600 space-y-1">
                    <p>• All cancelations must be made 72 hours before the start of the trip</p>
                    <p>• Any cancelation will incur a $10 non-refundable service fee</p>
                    <p>• To cancel your trip please contact the 'HELP' number listed on a users trips page</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <StripeProvider>
              <CheckoutForm tripData={tripData} />
            </StripeProvider>
          </div>
        </div>
      </div>
    </RiderLayout>
  );
};

export default RiderCheckout;
